import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, VStack, HStack, Heading, Text, Input, Button, Icon,
  Tabs, TabList, Tab, TabPanels, TabPanel, Badge, Spinner,
  useToast, useColorModeValue, Divider, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiExternalLink, FiDatabase, FiCloud, FiUser } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../utils/supabase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import {
  getAriveStageLabel, getAriveStageColor, getAriveLeadStatusLabel,
  ARIVE_TRACKER_NAMES, buildAriveDeepLink,
} from '../constants/ariveConstants';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useAuth } from '../contexts/AuthContext';
import { useCrmUsers } from '../hooks/useCrmUsers';
import ImpersonationBar from '../components/shared/ImpersonationBar';

function formatDate(d) {
  if (!d) return '--';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

function formatCurrency(v) {
  if (v == null) return '--';
  return `$${Number(v).toLocaleString()}`;
}

function DataRow({ label, value }) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  if (!value || value === '--') return null;
  return (
    <HStack justify="space-between" py={0.5}>
      <Text fontSize="xs" color={mutedColor}>{label}</Text>
      <Text fontSize="xs" fontWeight="medium" textAlign="right" maxW="60%">{value}</Text>
    </HStack>
  );
}

function JsonBlock({ data }) {
  const bg = useColorModeValue('gray.50', 'gray.900');
  return (
    <Box bg={bg} p={3} borderRadius="md" maxH="500px" overflowY="auto" fontSize="xs">
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(data, null, 2)}</pre>
    </Box>
  );
}

export default function AriveTestPage() {
  const { currentUser, SuperAdmin } = useAuth();
  const { canImpersonate, effectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const { users: crmUsers, loading: crmUsersLoading } = useCrmUsers();

  const [ariveId, setAriveId] = useState('');
  const [entityType, setEntityType] = useState('loan');
  const [loading, setLoading] = useState(false);
  const [ariveData, setAriveData] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [creditScores, setCreditScores] = useState(null);
  const [fetchedType, setFetchedType] = useState(null);
  const [error, setError] = useState(null);
  const [localDeals, setLocalDeals] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [mirrorMeta, setMirrorMeta] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [staleCache, setStaleCache] = useState(false);
  const [borrowerMatches, setBorrowerMatches] = useState([]);
  const [importing, setImporting] = useState(false);

  const [targetUserData, setTargetUserData] = useState(null);
  const toast = useToast();

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const impBg = useColorModeValue('purple.50', 'purple.900');

  useEffect(() => {
    const uid = effectiveUserId || currentUser?.uid;
    if (!uid) return;
    const unsubscribe = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (snap.exists()) setTargetUserData(snap.data());
    });
    return () => unsubscribe();
  }, [effectiveUserId, currentUser?.uid]);

  const ariveEnabled = targetUserData?.ariveEnabled === true;
  const hasCredentials = !!(targetUserData?.ariveCredentials?.clientId);
  const effectiveName = isImpersonating ? (impersonatedUser?.name || impersonatedUser?.email) : (currentUser?.displayName || currentUser?.email);

  const fetchFromArive = useCallback(async () => {
    const id = ariveId.trim();
    if (!id) { toast({ title: 'Enter an Arive ID', status: 'warning', duration: 2000 }); return; }

    setLoading(true);
    setError(null);
    setAriveData(null);
    setRawData(null);
    setCreditScores(null);
    setFetchedType(null);
    setMirrorMeta(null);
    setFromCache(false);
    setStaleCache(false);
    setBorrowerMatches([]);

    try {
      const fn = httpsCallable(functionsUsEast1, 'lookupAriveEntity');
      const payload = { ariveId: id, entityType };
      if (isImpersonating && effectiveUserId) {
        payload.targetUserId = effectiveUserId;
      }
      const result = await fn(payload);
      setAriveData(result.data.parsed);
      setRawData(result.data.raw);
      setCreditScores(result.data.creditScores);
      setFetchedType(result.data.entityType);
      setMirrorMeta(result.data.mirrorMeta || null);
      setFromCache(result.data.fromCache || false);
      setStaleCache(result.data.staleCache || false);

      // Cross-reference: find other Arive loans with same borrower email
      const borrowerEmail = result.data.parsed?.borrowerEmail;
      if (borrowerEmail && isSupabaseConfigured()) {
        const { data: matches } = await supabase
          .from('arive_loans')
          .select('arive_loan_id, entity_type, current_stage, base_loan_amount, borrower_first_name, borrower_last_name, linked_deal_id')
          .eq('borrower_email', borrowerEmail)
          .neq('arive_loan_id', id)
          .limit(10);
        setBorrowerMatches(matches || []);
      }

      toast({
        title: `Arive ${result.data.entityType} data loaded`,
        description: result.data.fromCache ? 'Served from cache' : 'Fresh from Arive API',
        status: 'success',
        duration: 3000,
      });
    } catch (err) {
      const msg = err.message || 'Unknown error';
      if (entityType === 'loan' && (msg.includes('not-found') || msg.includes('Not found'))) {
        setError(`Loan #${id} not found. This may not be in ${effectiveName}'s Arive pipeline. Try switching to "Lead" if it's a lead ID.`);
      } else if (msg.includes('No Arive credentials')) {
        setError(`No Arive credentials configured for ${effectiveName}. Go to CRM Settings > Arive LOS to add API keys.`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [ariveId, entityType, effectiveUserId, isImpersonating, effectiveName, toast]);

  const searchLocalDeals = useCallback(async () => {
    const id = ariveId.trim();
    if (!id || !isSupabaseConfigured()) { setLocalDeals([]); return; }
    setLocalLoading(true);
    try {
      const { data } = await supabase
        .from('crm_deals')
        .select('id, deal_name, stage, arive_id, arive_entity_type, arive_last_synced_at, amount')
        .eq('arive_id', id);
      setLocalDeals(data || []);
    } catch (_) { setLocalDeals([]); }
    finally { setLocalLoading(false); }
  }, [ariveId]);

  const handleSearch = () => { fetchFromArive(); searchLocalDeals(); };

  const d = ariveData || {};
  const deepLink = buildAriveDeepLink(fetchedType || entityType, ariveId.trim(), d.deepLinkURL);

  // Dev tool: admin-only under the company-sync model (lookups run under the
  // company client, so this page could otherwise expose any loan).
  const isAdmin = SuperAdmin === true || currentUser?.CRMRole === 'CRM Admin';
  if (!isAdmin) {
    return (
      <Container maxW="5xl" py={20}>
        <VStack spacing={4}>
          <Heading size="md" color="red.500">Access Denied</Heading>
          <Text color={mutedColor}>This developer tool is only available to administrators.</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="5xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Heading size="lg">Arive Lookup</Heading>
            <Text fontSize="sm" color={mutedColor}>Fetch any Arive Lead or Loan by ID using per-user credentials</Text>
          </VStack>
          <Badge colorScheme="purple" fontSize="xs" px={3} py={1}>DEV TOOL</Badge>
        </HStack>

        {/* Impersonation Bar */}
        {canImpersonate && (
          <ImpersonationBar users={crmUsers} loading={crmUsersLoading} label="Use credentials of" />
        )}

        {/* Credential Status */}
        <Box bg={isImpersonating ? impBg : bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" px={4} py={3}>
          <HStack spacing={3}>
            <Icon as={FiUser} color={ariveEnabled ? 'green.500' : 'gray.400'} />
            <VStack align="start" spacing={0} flex={1}>
              <Text fontSize="sm" fontWeight="medium">
                {isImpersonating ? `Impersonating: ${effectiveName}` : effectiveName}
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                {ariveEnabled ? 'Arive credentials active' : hasCredentials ? 'Arive credentials saved but disabled' : 'No Arive credentials configured'}
              </Text>
            </VStack>
            <HStack spacing={2}>
              <Badge colorScheme={ariveEnabled ? 'green' : hasCredentials ? 'yellow' : 'gray'} fontSize="10px">
                {ariveEnabled ? 'Connected' : hasCredentials ? 'Disabled' : 'Not Set Up'}
              </Badge>
              {ariveEnabled && (
                <Button
                  size="xs" variant="outline" colorScheme="purple"
                  leftIcon={<Icon as={FiDatabase} boxSize={3} />}
                  isLoading={importing}
                  loadingText="Importing..."
                  onClick={async () => {
                    setImporting(true);
                    try {
                      const fn = httpsCallable(functionsUsEast1, 'importAriveUserPipeline');
                      const payload = { enrichDetails: true };
                      if (isImpersonating && effectiveUserId) payload.targetUserId = effectiveUserId;
                      const result = await fn(payload);
                      toast({
                        title: 'Pipeline imported!',
                        description: `${result.data.loansFound} loans, ${result.data.leadsFound} leads. ${result.data.enriched} enriched.`,
                        status: 'success',
                        duration: 8000,
                      });
                    } catch (err) {
                      toast({ title: 'Import failed', description: err.message, status: 'error', duration: 5000 });
                    } finally {
                      setImporting(false);
                    }
                  }}
                >
                  Import Pipeline
                </Button>
              )}
            </HStack>
          </HStack>
        </Box>

        {/* Search Bar */}
        <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <VStack spacing={4}>
            <HStack w="100%">
              <Input
                placeholder="Enter Arive ID (e.g. 13506674)"
                value={ariveId}
                onChange={(e) => setAriveId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                size="lg" flex={1} autoFocus
              />
              <Button colorScheme="blue" size="lg" leftIcon={<Icon as={FiSearch} />}
                onClick={handleSearch} isLoading={loading} px={8}>
                Search
              </Button>
            </HStack>
            <HStack w="100%" spacing={4}>
              <HStack>
                <Text fontSize="sm" color={mutedColor}>Type:</Text>
                <Button size="xs" variant={entityType === 'loan' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setEntityType('loan')}>Loan</Button>
                <Button size="xs" variant={entityType === 'lead' ? 'solid' : 'outline'} colorScheme="purple" onClick={() => setEntityType('lead')}>Lead</Button>
              </HStack>
              {deepLink && (
                <Button as="a" href={deepLink} target="_blank" rel="noopener noreferrer"
                  size="xs" variant="outline" colorScheme="blue" leftIcon={<Icon as={FiExternalLink} />}>
                  Open in Arive
                </Button>
              )}
            </HStack>
          </VStack>
        </Box>

        {error && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon /><Text fontSize="sm">{error}</Text>
          </Alert>
        )}

        {/* Local Origin Matches */}
        {localDeals !== null && (
          <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
            <HStack mb={3}>
              <Icon as={FiDatabase} color={localDeals.length > 0 ? 'green.500' : 'gray.400'} />
              <Text fontWeight="semibold" fontSize="sm">Origin CRM</Text>
              <Badge colorScheme={localDeals.length > 0 ? 'green' : 'gray'} fontSize="10px">
                {localDeals.length > 0 ? `${localDeals.length} deal${localDeals.length > 1 ? 's' : ''} linked` : 'Not linked'}
              </Badge>
              {localLoading && <Spinner size="xs" />}
            </HStack>
            {localDeals.length > 0 ? (
              <Table size="sm" variant="simple">
                <Thead><Tr>
                  <Th fontSize="10px">Deal</Th><Th fontSize="10px">Stage</Th>
                  <Th fontSize="10px">Amount</Th><Th fontSize="10px">Type</Th><Th fontSize="10px">Last Synced</Th>
                </Tr></Thead>
                <Tbody>
                  {localDeals.map((deal) => (
                    <Tr key={deal.id}>
                      <Td fontSize="xs"><a href={`/crm/deals/${deal.id}`} style={{ color: 'var(--chakra-colors-blue-500)', textDecoration: 'underline' }}>{deal.deal_name || deal.id.substring(0, 8)}</a></Td>
                      <Td><Badge fontSize="10px">{deal.stage}</Badge></Td>
                      <Td fontSize="xs">{formatCurrency(deal.amount)}</Td>
                      <Td><Badge fontSize="10px" colorScheme={deal.arive_entity_type === 'loan' ? 'blue' : 'purple'}>{deal.arive_entity_type || '--'}</Badge></Td>
                      <Td fontSize="xs">{deal.arive_last_synced_at ? formatDate(deal.arive_last_synced_at) : 'Never'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text fontSize="xs" color={mutedColor}>No Origin deals linked to this Arive ID</Text>
            )}
          </Box>
        )}

        {/* Mirror Metadata */}
        {mirrorMeta && (
          <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" px={4} py={3}>
            <HStack spacing={4} flexWrap="wrap">
              <HStack spacing={1}>
                <Icon as={FiDatabase} color="blue.500" boxSize={3.5} />
                <Text fontSize="xs" fontWeight="semibold">Mirror Cache</Text>
              </HStack>
              <Badge colorScheme={staleCache ? 'yellow' : fromCache ? 'green' : 'blue'} fontSize="10px">
                {staleCache ? 'Stale cache (another user fetched this)' : fromCache ? 'Served from cache' : 'Fresh from API'}
              </Badge>
              {mirrorMeta.lastFetchedAt && (
                <Text fontSize="xs" color={mutedColor}>
                  Last fetched: {formatDate(mirrorMeta.lastFetchedAt)}
                </Text>
              )}
              {mirrorMeta.fetchCount > 0 && (
                <Text fontSize="xs" color={mutedColor}>
                  Fetched {mirrorMeta.fetchCount}x
                </Text>
              )}
              {mirrorMeta.contributorUserIds && mirrorMeta.contributorUserIds.length > 0 && (
                <Text fontSize="xs" color={mutedColor}>
                  {mirrorMeta.contributorUserIds.length} user{mirrorMeta.contributorUserIds.length > 1 ? 's' : ''} have access
                </Text>
              )}
              {mirrorMeta.linkedDealId && (
                <Badge colorScheme="green" fontSize="10px">
                  <a href={`/crm/deals/${mirrorMeta.linkedDealId}`} style={{ textDecoration: 'underline' }}>Linked to deal</a>
                </Badge>
              )}
              {!mirrorMeta.linkedDealId && (
                <Badge colorScheme="gray" fontSize="10px">Not linked to Origin deal</Badge>
              )}
            </HStack>
          </Box>
        )}

        {/* Borrower Cross-Reference */}
        {borrowerMatches.length > 0 && (
          <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md" px={4} py={3}>
            <Text fontSize="xs" fontWeight="semibold" mb={2}>Other Arive loans for same borrower</Text>
            <HStack spacing={3} flexWrap="wrap">
              {borrowerMatches.map((m) => (
                <Badge key={m.arive_loan_id} colorScheme="purple" fontSize="10px" px={2} py={1} cursor="pointer"
                  onClick={() => { setAriveId(m.arive_loan_id); }}>
                  #{m.arive_loan_id} - {m.current_stage || m.entity_type} {m.base_loan_amount ? `($${Number(m.base_loan_amount).toLocaleString()})` : ''}
                </Badge>
              ))}
            </HStack>
          </Box>
        )}

        {/* Arive Data */}
        {ariveData && (
          <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Icon as={FiCloud} color="blue.500" />
                <Heading size="sm">Arive {fetchedType === 'lead' ? 'Lead' : 'Loan'} #{d.ariveLoanId || d.ariveLeadId || ariveId}</Heading>
                {d.currentStage && <Badge colorScheme={getAriveStageColor(d.currentStage)}>{getAriveStageLabel(d.currentStage)}</Badge>}
                {d.leadStatus && <Badge colorScheme="purple">{getAriveLeadStatusLabel(d.leadStatus)}</Badge>}
              </HStack>

              <Tabs size="sm" variant="enclosed-colored" colorScheme="blue">
                <TabList flexWrap="wrap">
                  <Tab fontSize="xs">Summary</Tab>
                  <Tab fontSize="xs">Financials</Tab>
                  <Tab fontSize="xs">Key Dates</Tab>
                  <Tab fontSize="xs">Trackers</Tab>
                  <Tab fontSize="xs">Team & Contacts</Tab>
                  <Tab fontSize="xs">Credit</Tab>
                  <Tab fontSize="xs">Stage History</Tab>
                  <Tab fontSize="xs">Raw JSON</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack align="stretch" spacing={0}>
                      <DataRow label="Arive ID" value={String(d.ariveLoanId || d.ariveLeadId || '')} />
                      <DataRow label="System GUID" value={d.sysGUID} />
                      <DataRow label="CRM Reference ID" value={d.crmReferenceId} />
                      <DataRow label="Loan Purpose" value={d.loanPurpose} />
                      <DataRow label="Mortgage Type" value={d.mortgageType} />
                      <DataRow label="Borrower" value={[d.borrowerFirstName, d.borrowerLastName].filter(Boolean).join(' ')} />
                      <DataRow label="Borrower Email" value={d.borrowerEmail} />
                      <DataRow label="Borrower Phone" value={d.borrowerPhone} />
                      <DataRow label="Property" value={[d.propertyStreet, d.propertyCity, d.propertyState, d.propertyZip].filter(Boolean).join(', ')} />
                      <DataRow label="Property Type" value={d.propertyType} />
                      <DataRow label="Property Usage" value={d.propertyUsage} />
                      <DataRow label="Lender" value={d.lenderName} />
                      <DataRow label="Lender Product" value={d.lenderProductName} />
                      <DataRow label="Assignee" value={d.assigneeEmail} />
                      <DataRow label="Lead Source" value={d.leadSource} />
                      <DataRow label="Archived" value={d.archiveIndicator ? 'Yes' : null} />
                      <DataRow label="Created" value={formatDate(d.createdAt)} />
                      <DataRow label="Updated" value={formatDate(d.updatedAt)} />
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <VStack align="stretch" spacing={0}>
                      <DataRow label="Base Loan Amount" value={formatCurrency(d.baseLoanAmount)} />
                      <DataRow label="Total Loan Amount" value={formatCurrency(d.totalLoanAmount)} />
                      <DataRow label="Purchase Price" value={formatCurrency(d.purchasePrice)} />
                      <DataRow label="Down Payment" value={formatCurrency(d.downPayment)} />
                      <DataRow label="Note Rate" value={d.noteRate != null ? `${d.noteRate}%` : null} />
                      <DataRow label="LTV" value={d.ltv != null ? `${d.ltv}%` : null} />
                      <DataRow label="Back-End DTI" value={d.backEndDTI != null ? `${d.backEndDTI}%` : null} />
                      <DataRow label="Front-End DTI" value={d.frontEndDTI != null ? `${d.frontEndDTI}%` : null} />
                      <DataRow label="FICO" value={d.fico} />
                      <DataRow label="Estimated FICO" value={d.estimatedFICO} />
                      <DataRow label="Lock Status" value={d.lockStatus != null ? String(d.lockStatus) : null} />
                      <DataRow label="Lock Date" value={formatDate(d.lockDate)} />
                      <DataRow label="Lock Expiration" value={formatDate(d.lockExpirationDate)} />
                      <Divider my={2} />
                      <DataRow label="Gross Revenue" value={formatCurrency(d.grossLoanRevenue)} />
                      <DataRow label="Net Revenue" value={formatCurrency(d.netLoanRevenue)} />
                      <DataRow label="Compensation" value={d.compensation} />
                      <DataRow label="Broker Fee" value={formatCurrency(d.brokerFee)} />
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    {d.keyDates && Object.entries(d.keyDates).filter(([, v]) => v).length > 0 ? (
                      <VStack align="stretch" spacing={0}>
                        {Object.entries(d.keyDates).filter(([, v]) => v).map(([key, value]) => (
                          <DataRow key={key} label={key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()} value={formatDate(value)} />
                        ))}
                      </VStack>
                    ) : <Text fontSize="xs" color={mutedColor}>No key dates available</Text>}
                  </TabPanel>
                  <TabPanel>
                    {d.loanTrackers && d.loanTrackers.length > 0 ? (
                      <VStack align="stretch" spacing={2}>
                        {d.loanTrackers.map((t, i) => (
                          <HStack key={i} justify="space-between">
                            <Text fontSize="xs" color={mutedColor}>{ARIVE_TRACKER_NAMES[t.name] || t.displayName || t.name}</Text>
                            <Badge fontSize="10px" colorScheme={
                              ['RECEIVED','COMPLETE','SIGNED','APPROVED','FUNDED'].some(s => (t.currentTrackerStatus?.name || '').toUpperCase().includes(s)) ? 'green' :
                              ['ORDERED','SENT','PENDING','IN_PROGRESS'].some(s => (t.currentTrackerStatus?.name || '').toUpperCase().includes(s)) ? 'blue' : 'gray'
                            }>
                              {t.currentTrackerStatus?.displayName || t.currentTrackerStatus?.name || 'Unknown'}
                            </Badge>
                          </HStack>
                        ))}
                      </VStack>
                    ) : <Text fontSize="xs" color={mutedColor}>No trackers available</Text>}
                  </TabPanel>
                  <TabPanel>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={mutedColor}>Loan Team</Text>
                      <DataRow label="Originator" value={d.loanOriginatorName} />
                      <DataRow label="Originator Email" value={d.loanOriginatorEmail} />
                      <DataRow label="Processor" value={d.loanProcessorName} />
                      <DataRow label="Processor Email" value={d.loanProcessorEmail} />
                      <DataRow label="LOA" value={d.loanOfficerAssistantName} />
                      <DataRow label="LOA Email" value={d.loanOfficerAssistantEmail} />
                      {d.loanTeam && d.loanTeam.length > 0 && d.loanTeam.map((m, i) => (
                        <DataRow key={i} label={m.loanRole || 'Team'} value={`${m.firstName || ''} ${m.lastName || ''} ${m.emailAddressText ? `(${m.emailAddressText})` : ''}`.trim()} />
                      ))}
                      {d.businessContacts && d.businessContacts.length > 0 && (
                        <>
                          <Divider my={2} />
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={mutedColor}>Business Contacts</Text>
                          {d.businessContacts.map((bc, i) => (
                            <DataRow key={i} label={bc.role || 'Contact'} value={`${bc.firstName || ''} ${bc.lastName || ''} ${bc.companyName ? `- ${bc.companyName}` : ''} ${bc.emailAddressText ? `(${bc.emailAddressText})` : ''}`.trim()} />
                          ))}
                        </>
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    {creditScores && creditScores.length > 0 ? (
                      <VStack align="stretch" spacing={4}>
                        {creditScores.map((b, i) => (
                          <VStack key={i} align="stretch" spacing={1}>
                            <Text fontSize="xs" fontWeight="semibold">{b.BorrowerName} ({b.BorrowerRole})</Text>
                            <HStack spacing={8}>
                              {b.CreditScoreDetails?.experian != null && <VStack spacing={0}><Text fontSize="10px" color={mutedColor}>Experian</Text><Text fontSize="xl" fontWeight="bold">{b.CreditScoreDetails.experian}</Text></VStack>}
                              {b.CreditScoreDetails?.equifax != null && <VStack spacing={0}><Text fontSize="10px" color={mutedColor}>Equifax</Text><Text fontSize="xl" fontWeight="bold">{b.CreditScoreDetails.equifax}</Text></VStack>}
                              {b.CreditScoreDetails?.transunion != null && <VStack spacing={0}><Text fontSize="10px" color={mutedColor}>TransUnion</Text><Text fontSize="xl" fontWeight="bold">{b.CreditScoreDetails.transunion}</Text></VStack>}
                            </HStack>
                          </VStack>
                        ))}
                      </VStack>
                    ) : <Text fontSize="xs" color={mutedColor}>{creditScores === null ? 'Credit scores fetched automatically with loan data' : 'No credit data available'}</Text>}
                  </TabPanel>
                  <TabPanel>
                    {d.loanStatusHistory && d.loanStatusHistory.length > 0 ? (
                      <VStack align="stretch" spacing={1}>
                        {d.loanStatusHistory.map((s, i) => (
                          <HStack key={i} justify="space-between">
                            <Badge fontSize="10px" colorScheme={getAriveStageColor(s.status)}>{getAriveStageLabel(s.status)}</Badge>
                            <Text fontSize="xs" color={mutedColor}>{formatDate(s.date)}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    ) : <Text fontSize="xs" color={mutedColor}>No stage history available</Text>}
                  </TabPanel>
                  <TabPanel>
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="xs" fontWeight="bold">Parsed (what the Hub stores)</Text>
                      <JsonBlock data={ariveData} />
                      <Divider />
                      <Text fontSize="xs" fontWeight="bold">Raw Arive API Response</Text>
                      <JsonBlock data={rawData} />
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
